import {ReactNode} from "react";
import {UseFormReturn} from "react-hook-form";
import {IconType} from "react-icons";

import {AlertVariant} from "@/components/alert/Alert.types.ts";
import {DotVariant} from "@/components/dot/Dot.types.ts";
import {AnalyzerSource, AnalyzerTokenPricing, AnalyzerTurnType} from "@/lib/queries/analyzerRuns.ts";
import {GroupCheckDownCondition, MonitoringServiceType, ServiceTag} from "@/lib/types/checks.ts";
import {ContactGroup} from "@/lib/types/contacts.ts";

export interface SSETokenUsage {
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens?: number;
  cache_write_tokens?: number;
  total_tokens?: number;
  pricing?: AnalyzerTokenPricing;
}

export interface AggregatedUsage extends SSETokenUsage {
  elapsed: number;
}

export enum SSEEventType {
  STARTED = "started",
  THINKING = "thinking",
  TOOL_CALL = "tool_call",
  TOOL_RESULT = "tool_result",
  MESSAGE = "message",
  PROPOSING = "proposing",
  REPORT = "report",
  INTERVIEW = "interview",
  ANSWER = "answer",
  CHECK_SELECTION = "check_selection",
  ERROR = "error",
  DONE = "done",
}

export interface SSEEventBase {
  type: SSEEventType;
  timestamp: string;
  usage?: SSETokenUsage;
}

export interface SSEStartedEvent extends SSEEventBase {
  type: SSEEventType.STARTED;
  thread_id: string;
}

export interface SSEThinkingEvent extends SSEEventBase {
  type: SSEEventType.THINKING;
}

export interface SSEProposingEvent extends SSEEventBase {
  type: SSEEventType.PROPOSING;
}

export interface SSEToolCallEvent extends SSEEventBase {
  type: SSEEventType.TOOL_CALL;
  tool: string;
  args: Record<string, unknown>;
  call_id: string;
}

export interface SSEToolResultEvent extends SSEEventBase {
  type: SSEEventType.TOOL_RESULT;
  tool: string;
  result: string;
  call_id: string;
  status_code?: number;
  suspicious?: boolean;
  error?: boolean;
  screenshot?: {
    base64: string;
    mime_type: string;
  };
}

export interface SSEMessageEvent extends SSEEventBase {
  type: SSEEventType.MESSAGE;
  content: string;
  status?: string;
  status_done?: string;
  proposal_summary?: boolean;
}

export enum ReportSectionType {
  SITE_ARCHITECTURE = "site_architecture",
  DEPENDENCIES = "dependencies",
}

export enum AlertPriority {
  P1 = "P1",
  P2 = "P2",
  P3 = "P3",
  P4 = "P4",
}

export enum ProbeRegion {
  US_EAST = "US East",
  US_CENTRAL = "US Central",
  US_WEST = "US West",
  SOUTH_AMERICA = "South America",
  UK = "UK",
  EUROPE = "Europe",
  AFRICA = "Africa",
  ASIA = "Asia",
  OCEANIA = "Oceania",
}

export interface StatusPageComponent {
  name: string;
  covers: string;
}

export interface StatusPage {
  stakeholders: string;
  existing_host?: string;
  components: StatusPageComponent[];
}

export enum BusinessFunctionLabel {
  PUBLIC_WEBSITE_HEALTH = "Public Website Health",
  EMAIL_DOMAIN_TRUST = "Email & Domain Trust",
  INFRASTRUCTURE_DEPENDENCIES = "Infrastructure Dependencies",
}

export enum EscalationTier {
  TIER_1 = "Tier 1",
  TIER_2 = "Tier 2",
  TIER_3 = "Tier 3",
  TIER_4 = "Tier 4",
}

export interface AnalyzerEscalationTier {
  tier: EscalationTier;
  waitTime: string;
  channels: string;
  contacts: string;
}

export interface AnalyzerAlertPriorityDisplay {
  label: string;
  escalation: string;
}

export interface AnalyzerReportBadgeDisplay {
  label: string;
  dot: DotVariant;
}

interface AnalyzerSensitivityDefaultCells {
  label: string;
  types: MonitoringServiceType[];
  sensitivity: string;
  retries: string;
}

export type AnalyzerSensitivityDefaultRow = AnalyzerSensitivityDefaultCells & ({locations: number} | {probes: string});

export interface ReportBusinessFunctionGroup {
  label: string;
  tagged: boolean;
  checks: ReportOnlyCheck[];
}

export interface ProposedGroup {
  id: string;
  label: string;
  name: string;
  condition: GroupCheckDownCondition;
  member_ids: string[];
  manual_rows: number[];
  creatable: boolean;
  tags?: string[];
  contact_groups?: string[];
}

export enum CheckTypeFamily {
  WEBSITE = "website availability",
  DNS_SSL = "DNS and SSL integrity",
  MAIL = "mail delivery",
  DOMAIN = "domain expiry and reputation",
  API_ENDPOINTS = "API endpoints",
  USER_JOURNEYS = "user journeys",
  PAGE_PERFORMANCE = "page performance",
  REAL_USER_EXPERIENCE = "real-user experience",
  DEPENDENCIES = "third-party dependencies",
  PROCESS = "process monitoring",
}

export enum ReportBadgeStatus {
  SUCCESS = "success",
  ERROR = "error",
}

export interface ReportValue {
  value: string;
  secondary?: string;
  expires_at?: string;
}

export interface ReportDomainOverview {
  domain: string;
  primary_website?: ReportValue;
  ssl_certificate?: ReportValue;
  registration?: ReportValue;
}

export enum ReportConfidence {
  CONFIRMED = "confirmed",
  LIKELY = "likely",
}

export enum ReportRowEmphasis {
  SUBTOTAL = "subtotal",
  TOTAL = "total",
}

export interface ReportRow {
  label: string;
  value: string;
  secondary?: string;
  badge_status?: ReportBadgeStatus;
  confidence?: ReportConfidence;
  position?: number;
  emphasis?: ReportRowEmphasis;
  detail?: string;
}

export interface ReportOnlyCheck {
  monitoring_service_type: MonitoringServiceType;
  msp_address: string;
  name: string;
  justification: string;
  steps?: string;
  business_function?: string;
  confidence?: ReportConfidence;
}

export type OrderableCheck = ReportOnlyCheck & {priority?: number};

export interface ReportSection {
  type: ReportSectionType;
  intro?: string;
  rows: ReportRow[];
}

export interface ReportTypeRationale {
  type: MonitoringServiceType;
  rationale: string;
}

export interface MonitoringReport {
  domain: string;
  executive_summary: string;
  domain_overview: ReportDomainOverview;
  report_only_checks: ReportOnlyCheck[];
  type_rationales: ReportTypeRationale[];
  sections: ReportSection[];
  alert_note: string;
  probe_regions: ProbeRegion[];
  probe_regions_reason: string;
  status_page: StatusPage;
}

export interface SSEReportEvent extends SSEEventBase {
  type: SSEEventType.REPORT;
  content: MonitoringReport;
}

export interface ReportSectionPreset {
  title: string;
  icon: IconType;
}

export interface ReportLink {
  label: string;
  to: string;
}

export enum ReportRowField {
  POSITION = "position",
  LABEL = "label",
  VALUE = "value",
  SECONDARY = "secondary",
  DETAIL = "detail",
}

export enum ReportTableVariant {
  DEFAULT = "default",
  CHECKS = "checks",
  SUMMARY = "summary",
  TIERS = "tiers",
  PRIORITY = "priority",
}

export interface ReportTableColumn {
  field: ReportRowField;
  title: string;
}

export interface ReportDisplayTable {
  title?: string;
  variant?: ReportTableVariant;
  columns: ReportTableColumn[];
  rows: ReportRow[];
  rationale?: string;
  note?: string;
}

export interface ReportDisplaySection extends ReportSectionPreset {
  intro?: string;
  link?: ReportLink;
  rows: ReportRow[];
  tables?: ReportDisplayTable[];
}

export interface ReportTag {
  label: string;
  dot?: DotVariant;
}

export interface ReportPdfBadge {
  label: string;
  status: ReportBadgeStatus;
}

export type ReportPdfRow = Omit<ReportRow, "badge_status" | "confidence"> & {badge?: ReportPdfBadge; chip?: string};

export interface ReportPdfLink {
  label: string;
  href: string;
}

export interface ReportPdfTableRow {
  cells: string[];
  emphasis?: ReportRowEmphasis;
}

export type ReportPdfTable = Omit<ReportDisplayTable, "columns" | "rows"> & {
  section_number?: string;
  columns: string[];
  rows: ReportPdfTableRow[];
};

export type ReportPdfSection = Omit<ReportDisplaySection, "icon" | "rows" | "link" | "tables"> & {
  section_number?: string;
  rows: ReportPdfRow[];
  link?: ReportPdfLink;
  tables?: ReportPdfTable[];
};

export interface ReportPdfPayload {
  title: string;
  domain: string;
  sections: ReportPdfSection[];
}

export interface AnalyzerReportProps {
  report: MonitoringReport;
}

export interface AnalyzerReportHeadingProps {
  report: MonitoringReport;
}

export interface AnalyzerReportRowProps {
  row: ReportRow;
  separated: boolean;
}

export interface AnalyzerReportTagProps {
  tag: ReportTag;
  className?: string;
}

export interface InterviewOption {
  label: string;
  description: string;
}

export interface SSEInterviewEvent extends SSEEventBase {
  type: SSEEventType.INTERVIEW;
  question: string;
  topic: string;
  options: InterviewOption[];
  checkpoint_id?: string;
  sub_checkpoint_id?: string;
  sub_checkpoint_ns?: string;
}

export interface SSEAnswerEvent extends SSEEventBase {
  type: SSEEventType.ANSWER;
  answer: string;
  checkpoint_id: string;
}

export enum SSERespondAction {
  REVISE = "revise",
}

export interface SSERespondOptions {
  checkpointId?: string;
  subCheckpointId?: string;
  subCheckpointNs?: string;
  action?: SSERespondAction;
  selectedIds?: string[];
  availableTags?: string[];
  availableContactGroups?: string[];
  currentTags?: string[];
  currentContactGroups?: string[];
  checkPositions?: Record<string, number>;
  createdIds?: string[];
}

export interface SSEStreamBody {
  url?: string;
  hidden_types?: MonitoringServiceType[];
  thread_id?: string;
  answer?: string;
  action?: SSERespondAction;
  selected_ids?: string[];
  available_tags?: string[];
  available_contact_groups?: string[];
  current_tags?: string[];
  current_contact_groups?: string[];
  check_positions?: Record<string, number>;
  created_ids?: string[];
  checkpoint_id?: string;
  sub_checkpoint_id?: string;
  sub_checkpoint_ns?: string;
}

export interface ProposedCheck {
  id: string;
  monitoring_service_type: MonitoringServiceType;
  msp_address: string;
  name: string;
  justification: string;
  business_function?: string;
  priority?: number;
  msp_interval?: number;
  msp_sensitivity?: string;
  num_locations?: number;
  msp_port?: string;
  msp_dns_record_type?: string;
  msp_expect_string?: string;
  tags?: string[];
  contact_groups?: string[];
  group_check_proposal_ids?: string[];
  group_check_down_condition?: GroupCheckDownCondition;
}

export type ProposedCheckColumnField = Exclude<
  keyof ProposedCheck,
  "id" | "priority" | "business_function" | "group_check_proposal_ids" | "group_check_down_condition"
>;

export interface CheckLimitTier {
  tierKey: string;
  limitName: string;
  tierLabel: string;
  isBasic: boolean;
  limit: number;
  used: number;
}

export type CheckLimitTierLookup = (type: MonitoringServiceType) => CheckLimitTier | null;

export interface CheckLimitStatus {
  tierKey: string;
  limitName: string;
  isBasic: boolean;
  limit: number;
  remaining: number;
  proposed: number;
  selected: number;
}

export interface AnalyzerUsageChip {
  tierKey: string;
  limitName: string;
  tierLabel: string;
  isBasic: boolean;
  used: number;
  limit: number;
  selected: number;
}

export enum AnalyzerUsageChipState {
  UNDER = "under",
  AT_LIMIT = "atLimit",
  OVER = "over",
}

export interface AnalyzerUsageChipProps {
  chip: AnalyzerUsageChip;
}

export interface AnalyzerProposalPatch {
  msp_interval?: number;
  num_locations?: number;
}

export interface RevisionFormValues {
  revision: string;
}

export interface AnalyzerTagValue {
  id?: number;
  tag: string;
  color_hex: string;
}

export interface AnalyzerCreateFormValues {
  tags: AnalyzerTagValue[];
  contactGroups: number[];
}

export interface UseAnalyzerCreateFormOptions {
  tags?: ServiceTag[];
  contactGroups?: ContactGroup[];
  analyzedDomain: string;
  timezone?: string;
}

export interface UseAnalyzerCreateFormReturn {
  methods: UseFormReturn<AnalyzerCreateFormValues>;
  resetCreateForm: () => void;
}

export interface NormalizedProposals {
  checks: ProposedCheck[];
  uniformTags?: string[];
  uniformContactGroups?: string[];
}

export interface BulkCreateChecksOutcome {
  createdByProposalId: Record<string, number>;
  uuidByProposalId: Record<string, string>;
  errorsByProposalId: Record<string, string>;
  tagIds: number[];
}

export interface AnalyzerCreatedSnippet {
  name: string;
  host: string;
  uuid: string;
}

export interface InterviewFormValues {
  answer: string;
}

export enum ChatItemType {
  MESSAGE = "message",
  USER_MESSAGE = "userMessage",
  TOOL_GROUP = "toolGroup",
  POLL = "poll",
  ANSWER_CHIP = "answerChip",
  NOTICE = "notice",
  SETTINGS = "settings",
  ERROR = "error",
}

export interface ChatToolCall {
  call: SSEToolCallEvent;
  result?: SSEToolResultEvent;
}

export interface ChatMessageItem {
  type: ChatItemType.MESSAGE;
  event: SSEMessageEvent;
}

export interface ChatUserMessageItem {
  type: ChatItemType.USER_MESSAGE;
  content: string;
}

export interface ChatToolGroupItem {
  type: ChatItemType.TOOL_GROUP;
  title?: string;
  titleDone?: string;
  calls: ChatToolCall[];
  isComplete: boolean;
}

export interface ChatPollItem {
  type: ChatItemType.POLL;
  event: SSEInterviewEvent;
}

export interface ChatAnswerChipItem {
  type: ChatItemType.ANSWER_CHIP;
  event: SSEInterviewEvent;
  answer: string;
  superseded: boolean;
}

export interface ChatNoticeItem {
  type: ChatItemType.NOTICE;
  success: boolean;
  callout: string;
  body: string;
}

export interface ChatSettingsItem {
  type: ChatItemType.SETTINGS;
}

export interface ChatErrorItem {
  type: ChatItemType.ERROR;
  message: string;
}

export type ChatItem =
  | ChatMessageItem
  | ChatUserMessageItem
  | ChatToolGroupItem
  | ChatPollItem
  | ChatAnswerChipItem
  | ChatNoticeItem
  | ChatSettingsItem
  | ChatErrorItem;

export interface AnalyzerChatItemProps {
  item: ChatItem;
  isLast?: boolean;
  className?: string;
}

export interface AnalyzerRetryAlertProps {
  callout: string;
  variant?: AlertVariant;
  icon?: IconType;
  retryable?: boolean;
  className?: string;
  children: ReactNode;
}

export interface AnalyzerRevisionNoticeProps {
  success: boolean;
  callout: string;
  body: string;
  className?: string;
}

export interface AnalyzerUserMessageProps {
  content: string;
  className?: string;
}

export interface AnalyzerChatMessageProps {
  content: string;
  className?: string;
}

export interface AnalyzerToolGroupProps {
  item: ChatToolGroupItem;
  isLast?: boolean;
  className?: string;
}

export interface AnalyzerToolGroupActionProps {
  toolCall: ChatToolCall;
  inProgress: boolean;
}

export type AnalyzerPollCardMode = "initial" | "edit";

export interface AnalyzerPollCardProps {
  event: SSEInterviewEvent;
  mode?: AnalyzerPollCardMode;
  className?: string;
}

export interface AnalyzerPollAnswerChipProps {
  event: SSEInterviewEvent;
  answer: string;
  superseded: boolean;
  className?: string;
}

export interface AnalyzerChecksTableProps {
  checks: ProposedCheck[];
  hideEmptyOptionalColumns?: boolean;
}

export interface AnalyzerResultsHeaderProps {
  activeTab: AnalyzerTab;
  setActiveTab: (tab: AnalyzerTab) => void;
}

export interface AnalyzerSetIntervalsModalProps {
  show: boolean;
  setShow: (show: boolean) => void;
}

export interface AnalyzerSetLocationsModalProps {
  show: boolean;
  setShow: (show: boolean) => void;
}

export interface AnalyzerSetLocationsFormValues {
  num_locations: number;
}

export interface AnalyzerSetLocationsFormProps {
  formId: string;
  onSubmit: (values: AnalyzerSetLocationsFormValues) => void;
  defaultNumLocations: number;
  maxNumLocations?: number;
}

export interface AnalyzerCreateConfirmDialogProps {
  show: boolean;
  actionLabel: string;
  isGrowOnly: boolean;
  rumCount: number;
  tags: AnalyzerTagValue[];
  loading: boolean;
  snippets: AnalyzerCreatedSnippet[];
  hasFailures: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  onGoToChecks: () => void;
}

export interface AnalyzerUpgradeModalProps {
  statuses: CheckLimitStatus[];
  canPurchase: boolean;
  onClose: () => void;
}

export interface AnalyzerOptionalSettingsProps {
  className?: string;
}

export interface AnalyzerSettingsTagsFieldProps {
  disabled?: boolean;
}

export interface AnalyzerSettingsContactsFieldProps {
  disabled?: boolean;
}

export interface AnalyzerSettingsChip {
  name: string;
  colorHex?: string;
}

export interface AnalyzerSettingsLock {
  title: string;
  example: string;
}

export interface AnalyzerSettingsFieldProps {
  label: string;
  subText: string;
  chips: AnalyzerSettingsChip[];
  onRemove: (name: string) => void;
  editing: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  locked?: AnalyzerSettingsLock;
  disabled?: boolean;
  error?: string;
  children: ReactNode;
}

export interface AnalyzerCheckCellProps {
  check: ProposedCheck;
}

export interface SSECheckSelectionEvent extends SSEEventBase {
  type: SSEEventType.CHECK_SELECTION;
  checks: ProposedCheck[];
  groups?: ProposedGroup[];
  outcome?: string;
  success?: boolean;
  checkpoint_id?: string;
  sub_checkpoint_id?: string;
  sub_checkpoint_ns?: string;
}

export interface SSEErrorEvent extends SSEEventBase {
  type: SSEEventType.ERROR;
  message: string;
  code?: string;
}

export interface SSEDoneEvent extends SSEEventBase {
  type: SSEEventType.DONE;
  elapsed: number;
  usage: SSETokenUsage;
}

export type SSEEvent =
  | SSEStartedEvent
  | SSEThinkingEvent
  | SSEProposingEvent
  | SSEToolCallEvent
  | SSEToolResultEvent
  | SSEMessageEvent
  | SSEReportEvent
  | SSEInterviewEvent
  | SSEAnswerEvent
  | SSECheckSelectionEvent
  | SSEErrorEvent
  | SSEDoneEvent;

export enum AnalyzerPhase {
  IDLE = "idle",
  STREAMING = "streaming",
  INTERVIEW = "interview",
  CHECK_SELECTION = "check_selection",
  DONE = "done",
  ERROR = "error",
  ABORTED = "aborted",
}

export interface AnalyzerToolLabel {
  active: string;
  complete: string;
  // Arg keys appended to the label, in order. Defaults to every string arg; set it for a tool
  // that takes an arg too long to read inline, such as a list of candidate hostnames.
  summaryArgs?: string[];
}

export enum AnalyzerTab {
  SELECTION = "selection",
  REPORT = "report",
}

export class AnalyzerAuthError extends Error {}

export interface AnalyzerRunTrackingState {
  threadId?: string;
  url: string;
  source?: AnalyzerSource;
  nextTurnIndex: number;
}

export interface AnalyzerTurnState {
  index: number;
  type: AnalyzerTurnType;
  failed: boolean;
  reported: boolean;
  doneEventReceived?: boolean;
  errorCode?: string;
  usage?: SSETokenUsage;
  lastUsage?: SSETokenUsage;
  elapsed?: number;
  proposalsCount?: number;
  revisionSuccess?: boolean;
}
